import { useSendConfirmation } from "@entities/brief/lib/hooks/useSendConfirmation";
import { ToastService } from "@shared/lib/services";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { Avatar, Form, Rate } from "antd";
import { Mail, Phone } from "lucide-react";
import { useEffect } from "react";
import { AddCommercials } from "./AddCommercials";
import styles from "./styles.module.scss";

import { useGetBriefDeliverable } from "@entities/brief/lib/hooks/useGetBriefDeliverable";
import { getInfoDetails } from "@entities/influencer/lib/utils/getInfoDetails";
import { formatData } from "@shared/lib/utils/formatData";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { Button } from "@shared/ui/Button";
import EmailPreview from "./EmailPreview";

interface Props {
  briefInfluencer: IBriefInfluencerV2;
  onClose: () => void;
}

export function BriefConfirmation({ briefInfluencer, onClose }: Props) {
  const creatorInfo = getInfoDetails(briefInfluencer);
  const contact = briefInfluencer.primaryContact;
  const contactPerson = contact?.agencyUserId ? contact.agencyUser : contact;
  const contactPhone = [contactPerson?.countryCode, contactPerson?.phoneNumber]
    .filter(Boolean)
    .join(" ");
  const contactEmail = contactPerson?.emailId || "";
  const [form] = Form.useForm();
  const costValue = Form.useWatch("cost", form);
  const confirmationNotesValue = Form.useWatch("confirmationNotes", form);
  const paymentTermsValue = Form.useWatch("paymentTerms", form);

  const { deliverable: delExact } = useGetBriefDeliverable(
    briefInfluencer?.deliverableV2Id || ""
  );

  const { sendConfirmation, isSending, isSent } = useSendConfirmation(
    briefInfluencer.briefInfluencerId
  );
  const handleSaveCommercials = () => {
    form
      .validateFields()
      .then((values) => {
        sendConfirmation({
          ...values,
          briefInfluencerId: briefInfluencer._id,
        });
      })
      .catch((err) => {});
  };

  useEffect(() => {
    form.setFieldsValue({
      paymentTerms: briefInfluencer?.paymentTerms || "",
    });
  }, [briefInfluencer?._id, briefInfluencer?.paymentTerms, form]);

  useEffect(() => {
    if (isSent) {
      ToastService.success("Confirmation sent successfully");
      onClose();
    }
  }, [isSent]);

  const delString = (() => {
    const purchaseItems = delExact?.deliverableV2?.purchaseItems;
    if (!purchaseItems?.length) return "";

    const contentItems = purchaseItems.filter(
      (item) => item?.purchaseItemType === "Content"
    );
    const adRightsItems = purchaseItems.filter(
      (item) => item?.purchaseItemType === "Ad-Rights"
    );

    // Build a map from purchase-item _id → display label (used to link ad rights)
    const contentLabelById = new Map<string, string>();

    const contentLines = contentItems.map((item, index) => {
      const configurator =
        item?.contentDeliverableConfigurator &&
        typeof item.contentDeliverableConfigurator === "object"
          ? (item.contentDeliverableConfigurator as { platform?: string })
          : undefined;
      const platform = configurator?.platform || "";
      const category = item?.contentCategory || "Content";
      const contentLabel = `${platform ? platform + " - " : ""}${category}`;

      if (item?._id) {
        contentLabelById.set(String(item._id), contentLabel);
      }

      const details = item?.contentSubType
        ? Object.entries(item.contentSubType)
            .map(([key, value]) => `${key}: ${value}`)
            .join(", ")
        : "";

      return `${index + 1}) ${platform ? platform + " - " : ""}${category}${details ? ` (${details})` : ""}`;
    });

    const adRightsLines = adRightsItems.map((item, ind) => {
      const rightsType = item?.adRightsType || "Ad Rights";
      const duration = item?.adRightsDurations || "";
      const rightsLabel = duration
        ? `${rightsType} · ${duration}`
        : rightsType;
      const linkedContentLabel = item?.adRightsLinkedToContentPurchaseItemId
        ? contentLabelById.get(String(item.adRightsLinkedToContentPurchaseItemId))
        : "";

      return linkedContentLabel
        ? `${contentItems.length + ind}) Ad Rights (${rightsLabel}) for ${linkedContentLabel}`
        : `${contentItems.length + ind}) Ad Rights (${rightsLabel})`;
    });

    const allLines = [...contentLines, ...adRightsLines];
    return allLines.length ? `${allLines.join(", ")}` : "";
  })();

  return (
    <>
      <div className={styles.confirmationLayout}>
        <div className={styles.formColumn}>
          <div className={styles.creatorCard}>
            <Avatar src={creatorInfo.dp} className={styles.avatar} size={52} />
            <div>
              <div className={styles.nameRow}>
                <div className={styles.name}>{creatorInfo.name}</div>
                {creatorInfo.isVerified ? (
                  <img src="/instaTick.svg" alt="" className={styles.verified} />
                ) : null}
              </div>
              <button
                type="button"
                className={styles.handle}
                onClick={() => window.open(creatorInfo?.link, "_blank")}
              >
                {getPlatformIcon(creatorInfo?.platform || "")}
                {creatorInfo?.username}
              </button>
              <div className={styles.rating}>
                <Rate disabled value={creatorInfo.rating} />
              </div>
            </div>
          </div>
          {contact ? (
            <div className={styles.recipientCard}>
              <div className={styles.recipientHead}>
                <div>
                  <p className={styles.cardLabel}>Sending to</p>
                  <div className={styles.contactName}>{contactPerson?.name}</div>
                </div>
                <div className={styles.contactTags}>
                  <span className={styles.typeTag}>{contact.type}</span>
                  {contact.isPrimaryContact ? (
                    <span className={styles.primaryTag}>Primary</span>
                  ) : null}
                  {contact.agency ? (
                    <span className={styles.agencyTag}>{contact.agency.name}</span>
                  ) : null}
                </div>
              </div>
              <div className={styles.recipientDetails}>
                {contactPhone ? (
                  <div className={styles.recipientRow}>
                    <Phone size={14} />
                    <span>{contactPhone}</span>
                  </div>
                ) : null}
                {contactEmail ? (
                  <div className={styles.recipientRow}>
                    <Mail size={14} />
                    <span>{contactEmail}</span>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          <AddCommercials form={form} briefInfluencer={briefInfluencer} />
        </div>
        <div className={styles.previewColumn}>
          <EmailPreview
            className={styles.emailPreview}
            fromEmailId="creators@cloutflow.com"
            toCreatorEmail={contactEmail}
            subject={`${briefInfluencer.brief?.name} - Cloutflow Confirmation`}
            influencerFirstName={creatorInfo.name}
            campaignName={briefInfluencer.brief?.name}
            brandName={briefInfluencer.brand?.name || ""}
            platform={creatorInfo.platform || ""}
            username={creatorInfo.username || ""}
            deliverables={delString}
            commercials={costValue ? `₹${formatData(costValue, "0,00")}` : ""}
            campaignTimeline={
              ""
            }
            paymentTerms={paymentTermsValue || briefInfluencer.paymentTerms || ""}
            confirmText="Confirm Campaign"
            deliverablesNotesHtml={
              briefInfluencer?.deliverableV2?.notes
            }
            confirmationNotes={confirmationNotesValue}
            termsUrl="https://docs.google.com/document/d/1xwxyDbaFExr5_sx1sCUpsHP95bPGj-JG6C-9MF341p4/edit?tab=t.0#heading=h.129t69oos65n"
          />
        </div>
      </div>
      <div className={styles.confirmationFooter}>
        <Button onClick={onClose}>Back</Button>
        <Button
          type="primary"
          onClick={handleSaveCommercials}
          loading={isSending}
        >
          Confirm
        </Button>
      </div>
    </>
  );
}
